
/* ==========================================================================
   APEX PRO SERIES — PREMIUM CART DRAWER v1.5
   Desktop and mobile | Shopify Debut

   - Live Cart Display Title metafields
   - Line-item totals and unit-price clarity
   - Shopify AJAX quantity updates and removal
   - Accessible trash-icon removal control
   - Premium empty-cart header state
   - Preserved drawer animations and keyboard handling
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-apex-cart-drawer]');
  if (!root) return;

  var panel = root.querySelector('[data-apex-drawer-panel]');
  var itemsNode = root.querySelector('[data-apex-drawer-items]');
  var emptyNode = root.querySelector('[data-apex-drawer-empty]');
  var footer = root.querySelector('[data-apex-drawer-footer]');
  var countNode = root.querySelector('[data-apex-drawer-count]');
  var subtotalNode = root.querySelector('[data-apex-drawer-subtotal]');
  var statusNode = root.querySelector('[data-apex-drawer-status]');
  var messageNode = root.querySelector('[data-apex-drawer-message]');

  var lastFocus = null;
  var lastOverflow = '';
  var busy = false;
  var closeTimer = null;

  var currency =
    (window.Shopify &&
      Shopify.currency &&
      Shopify.currency.active) || 'CAD';

  var base =
    (window.Shopify &&
      Shopify.routes &&
      Shopify.routes.root) || '/';

  var focusSelector =
    'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function money(cents) {
    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function request(url, options) {
    return fetch(
      url,
      Object.assign(
        { credentials: 'same-origin' },
        options || {}
      )
    ).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || data.status >= 400) {
          throw new Error(
            data.description ||
            data.message ||
            'Cart could not be updated.'
          );
        }
        return data;
      });
    });
  }

  function element(tag, className, content) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) {
      node.textContent = content;
    }
    return node;
  }

  function render(cart) {
    currency = cart.currency || currency;
    itemsNode.replaceChildren();

    /* Premium empty-cart header behaviour */
    var isEmpty = cart.item_count === 0;

    countNode.textContent = isEmpty
      ? ''
      : '(' + cart.item_count + ')';

    statusNode.hidden = isEmpty;
    statusNode.textContent = isEmpty
      ? ''
      : '✓ Added to your cart';

    subtotalNode.textContent =
      money(cart.total_price) + ' ' + currency;

    emptyNode.hidden = !isEmpty;
    footer.hidden = isEmpty;

    cart.items.forEach(function (item) {
      var row = element('article', 'apex-cart-drawer__item');

      var picture = element(
        'a',
        'apex-cart-drawer__item-image-link'
      );

      picture.href = item.url || '#';

      if (
        item.image ||
        (item.featured_image && item.featured_image.url)
      ) {
        var img = element('img', 'apex-cart-drawer__item-image');
        img.src = item.image || item.featured_image.url;
        img.alt = item.product_title || item.title;
        img.loading = 'lazy';
        picture.appendChild(img);
      }

      var details = element(
        'div',
        'apex-cart-drawer__item-details'
      );

      var title = element(
        'a',
        'apex-cart-drawer__item-title'
      );

      title.href = item.url || '#';

      var shortTitles = window.apexCartDisplayTitles || {};
      title.textContent =
        shortTitles[item.product_id] ||
        item.product_title ||
        item.title;

      details.appendChild(title);

      if (
        item.variant_title &&
        item.variant_title !== 'Default Title'
      ) {
        details.appendChild(
          element(
            'p',
            'apex-cart-drawer__item-variant',
            item.variant_title
          )
        );
      }

      if (
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan
      ) {
        details.appendChild(
          element(
            'p',
            'apex-cart-drawer__item-variant',
            item.selling_plan_allocation.selling_plan.name
          )
        );
      }

      /* Primary line-item total */
      var price = element(
        'p',
        'apex-cart-drawer__item-price',
        money(item.final_line_price)
      );

      details.appendChild(price);

      /* Unit-price clarity for quantities greater than one */
      if (item.quantity > 1) {
        var unitCents = item.final_line_price / item.quantity;
        var exactUnitPrice = Number.isInteger(unitCents);
        var unitPriceText = money(unitCents) + ' each';

        if (!exactUnitPrice) {
          unitPriceText = 'Avg. ' + unitPriceText;
        }

        var unitPrice = element(
          'p',
          'apex-cart-drawer__unit-price',
          unitPriceText
        );

        details.appendChild(unitPrice);
      }

      /* Quantity controls */
      var actions = element(
        'div',
        'apex-cart-drawer__item-actions'
      );

      var qty = element('div', 'apex-cart-drawer__qty');

      var minus = element('button', '', '−');
      minus.type = 'button';
      minus.dataset.apexQtyChange = '-1';
      minus.dataset.key = item.key;
      minus.setAttribute(
        'aria-label',
        'Decrease quantity of ' + item.product_title
      );
      minus.disabled = item.quantity <= 1;

      var number = element('span', '', String(item.quantity));
      number.setAttribute(
        'aria-label',
        'Quantity ' + item.quantity
      );

      var plus = element('button', '', '+');
      plus.type = 'button';
      plus.dataset.apexQtyChange = '1';
      plus.dataset.key = item.key;
      plus.setAttribute(
        'aria-label',
        'Increase quantity of ' + item.product_title
      );

      qty.append(minus, number, plus);

      /* Accessible trash icon; AJAX removal preserved */
      var remove = element(
        'button',
        'apex-cart-drawer__remove'
      );

      remove.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" ' +
        'width="18" height="18" viewBox="0 0 24 24" ' +
        'fill="none" stroke="currentColor" stroke-width="1.8" ' +
        'stroke-linecap="round" stroke-linejoin="round" ' +
        'aria-hidden="true" focusable="false">' +
        '<path d="M3 6h18"/>' +
        '<path d="M8 6V4h8v2"/>' +
        '<path d="M19 6l-1 14H6L5 6"/>' +
        '<path d="M10 10v7"/>' +
        '<path d="M14 10v7"/>' +
        '</svg>';

      remove.type = 'button';
      remove.dataset.apexRemove = '';
      remove.dataset.key = item.key;
      remove.setAttribute(
        'aria-label',
        'Remove ' + item.product_title
      );

      actions.append(qty, remove);
      details.appendChild(actions);
      row.append(picture, details);
      itemsNode.appendChild(row);
    });

    /* Synchronize the Debut header cart badge */
    var bubble = document.querySelector(
      '.site-header__cart-count'
    );

    if (bubble) {
      bubble.classList.toggle(
        'hide',
        cart.item_count === 0
      );

      var count = bubble.querySelector('span');
      if (count) {
        count.textContent = cart.item_count;
      }
    }
  }

  /* Load Cart Display Title metafields from Shopify section */
  function loadCartDisplayTitles() {
    return fetch(
      base + 'cart?section_id=apex-cart-titles',
      {
        credentials: 'same-origin',
        cache: 'no-store'
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Cart title section unavailable.');
        }
        return response.text();
      })
      .then(function (html) {
        var documentFragment =
          new DOMParser().parseFromString(html, 'text/html');

        var dataNode = documentFragment.querySelector(
          '[data-apex-cart-titles-json]'
        );

        if (!dataNode) {
          throw new Error('Cart title data not found.');
        }

        var titles = JSON.parse(dataNode.textContent);

        if (
          !titles ||
          typeof titles !== 'object' ||
          Array.isArray(titles)
        ) {
          throw new Error('Invalid cart title data.');
        }

        window.apexCartDisplayTitles = titles;
      });
  }

  function refresh() {
    return request(base + 'cart.js').then(function (cart) {
      return loadCartDisplayTitles()
        .catch(function (error) {
          console.warn(
            'APEX Cart Drawer: short titles unavailable.',
            error
          );
        })
        .then(function () {
          render(cart);
        });
    });
  }

  /* Open drawer; preserve existing behaviour */
  function open() {
    if (closeTimer !== null) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    root.classList.remove('apex-cart-drawer--closing');

    if (root.hidden) {
      lastFocus = document.activeElement;
      lastOverflow = document.body.style.overflow;
    }

    root.hidden = false;
    document.body.style.overflow = 'hidden';

    statusNode.hidden = false;
    statusNode.textContent = '✓ Added to your cart';
    messageNode.textContent = 'Loading your cart…';
    panel.focus();

    return refresh()
      .then(function () {
        messageNode.textContent = '';
      })
      .catch(function () {
        messageNode.textContent =
          'Unable to load cart. Please use View Cart.';
      });
  }

  /* Close drawer; preserve 500ms exit timing */
  function close() {
    if (root.hidden || closeTimer !== null) {
      return;
    }

    var restoreFocus = lastFocus;
    var restoreOverflow = lastOverflow;

    function finishClose() {
      root.hidden = true;
      root.classList.remove('apex-cart-drawer--closing');
      document.body.style.overflow = restoreOverflow;
      messageNode.textContent = '';
      closeTimer = null;

      if (
        restoreFocus &&
        typeof restoreFocus.focus === 'function'
      ) {
        restoreFocus.focus({ preventScroll: true });
      }
    }

    if (
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches
    ) {
      finishClose();
      return;
    }

    root.classList.add('apex-cart-drawer--closing');
    closeTimer = setTimeout(finishClose, 500);
  }

  /* Shopify AJAX quantity changes and item removal */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-apex-drawer-close]')) {
      close();
      return;
    }

    var button = e.target.closest(
      '[data-apex-qty-change],[data-apex-remove]'
    );

    if (!button || busy) return;

    var row = button.closest('.apex-cart-drawer__item');

    var oldQty = Number(
      row.querySelector('.apex-cart-drawer__qty span').textContent
    );

    var nextQty =
      button.hasAttribute('data-apex-remove')
        ? 0
        : oldQty + Number(button.dataset.apexQtyChange);

    if (
      nextQty < 1 &&
      !button.hasAttribute('data-apex-remove')
    ) {
      return;
    }

    busy = true;
    root.classList.add('apex-cart-drawer--busy');
    messageNode.textContent = 'Updating cart…';

    request(base + 'cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: button.dataset.key,
        quantity: nextQty
      })
    })
      .then(function (cart) {
        render(cart);
        messageNode.textContent = '';
      })
      .catch(function (err) {
        messageNode.textContent = err.message;
        refresh().catch(function () {});
      })
      .finally(function () {
        busy = false;
        root.classList.remove('apex-cart-drawer--busy');
      });
  });

  /* Keyboard accessibility and focus trapping */
  document.addEventListener('keydown', function (e) {
    if (root.hidden) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (e.key !== 'Tab') return;

    var focusable = Array.prototype.filter.call(
      panel.querySelectorAll(focusSelector),
      function (el) {
        return el.getClientRects().length > 0;
      }
    );

    if (!focusable.length) {
      e.preventDefault();
      panel.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (
      e.shiftKey &&
      (
        document.activeElement === first ||
        document.activeElement === panel
      )
    ) {
      e.preventDefault();
      last.focus();
    } else if (
      !e.shiftKey &&
      document.activeElement === last
    ) {
      e.preventDefault();
      first.focus();
    }
  });

  /* Public API used by the Debut integration */
  window.apexCartDrawer = {
    open: open,
    close: close,
    refresh: refresh
  };
})();
